import { Collapse, Form } from 'antd';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PRESET_FORM_ID } from '@modules/presets/lib/constants';
import type { PresetFormValues } from '@modules/presets/lib/types';
import { AdvancedSettings } from './sections/advanced-settings';
import { AnalyticsSettings } from './sections/analytics-settings';
import { AppearanceSettings } from './sections/appearance-settings';
import { BasicSettings } from './sections/basic-settings';
import { ContentSettings } from './sections/content-settings';
import { PreviewSettings } from './sections/preview-settings';
import styles from './preset-form.module.scss';

interface PresetFormProps {
  form: UseFormReturn<PresetFormValues>;
  disabled?: boolean;
  onSubmit: (values: PresetFormValues) => void | Promise<void>;
  onOpenMetrikaGuide: () => void;
}

export const PresetForm = ({ form, disabled, onSubmit, onOpenMetrikaGuide }: PresetFormProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'presets.editor.sections' });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const items = [
    {
      key: 'basic',
      label: t('basic'),
      children: <BasicSettings control={control} errors={errors} disabled={disabled} />,
    },
    {
      key: 'appearance',
      label: t('appearance'),
      children: <AppearanceSettings control={control} errors={errors} disabled={disabled} />,
    },
    {
      key: 'content',
      label: t('content'),
      children: <ContentSettings control={control} errors={errors} disabled={disabled} />,
    },
    {
      key: 'preview',
      label: t('preview'),
      children: <PreviewSettings control={control} errors={errors} disabled={disabled} />,
    },
    {
      key: 'analytics',
      label: t('analytics'),
      children: (
        <AnalyticsSettings
          control={control}
          errors={errors}
          disabled={disabled}
          onOpenGuide={onOpenMetrikaGuide}
        />
      ),
    },
    {
      key: 'advanced',
      label: t('advanced'),
      children: <AdvancedSettings control={control} errors={errors} disabled={disabled} />,
    },
  ];

  return (
    <Form
      id={PRESET_FORM_ID}
      layout="vertical"
      className={styles.form}
      onFinish={handleSubmit(onSubmit)}
    >
      <Collapse
        bordered={false}
        items={items}
        defaultActiveKey={items.map((item) => item.key)}
        expandIconPosition="end"
        className={styles.collapse}
      />
    </Form>
  );
};
